import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // {key,name,sub,url,photo,talentId}
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);

  if (!audioRef.current && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
  }

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.currentTime);
    const onDur = () => setDuration(a.duration || 0);
    const onEnd = () => { setPlaying(false); setProgress(0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onPlaying = () => setLoading(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("playing", onPlaying);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("playing", onPlaying);
    };
  }, []);

  const playTrack = useCallback((track) => {
    const a = audioRef.current;
    if (!a) return;
    if (current && current.key === track.key) {
      if (a.paused) { a.play(); } else { a.pause(); }
      return;
    }
    setCurrent(track);
    setProgress(0); setDuration(0); setLoading(true);
    a.src = track.url;
    a.play().catch(() => setLoading(false));
    if (window.gtag) window.gtag("event", "demo_played", { talent: track.name });
  }, [current]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) a.play(); else a.pause();
  }, [current]);

  const seek = useCallback((pct) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    a.currentTime = pct * a.duration;
    setProgress(a.currentTime);
  }, []);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.src = ""; }
    setCurrent(null); setPlaying(false); setProgress(0);
  }, []);

  const isActive = (key) => current && current.key === key;

  return (
    <PlayerContext.Provider value={{ current, playing, progress, duration, loading, playTrack, toggle, seek, stop, isActive }}>
      {children}
    </PlayerContext.Provider>
  );
}
